import { CreatorPlatformsType, ICreatorPlatformStatus } from '~/clientApi';
import { ALL_SOCIAL_PLATFORM_TYPE, SOCIAL_PLATFORMS } from '~/constants';
import { getPlatformProfileLink } from '~/core/utils';
import { getCreatorLocation, getMainPlatformData } from '~/core/utils/creator';
import { ICreator } from '~/types';
import { formatAbbr } from '~/utils';

export type EmailTemplPlatformsType = {
  [k in ALL_SOCIAL_PLATFORM_TYPE]?: ReturnType<
    typeof getEmailTemplPlatformData
  >;
};

const getEmailTemplPlatformData = (
  platform: ALL_SOCIAL_PLATFORM_TYPE,
  { followersCount, ...data }: ICreatorPlatformStatus,
) => ({
  ...data,
  followersCount: formatAbbr(followersCount),
  platformUrl: data.handle && getPlatformProfileLink(platform, data.handle),
});

export const getPlatformsDataForEmailTemp = (
  platforms: CreatorPlatformsType,
  isOffPlatform: boolean | undefined,
): EmailTemplPlatformsType =>
  Object.entries(platforms).reduce(
    (acc: EmailTemplPlatformsType, [platform, data]) => {
      if (
        data.isConnected ||
        (isOffPlatform && data.followersCount) ||
        data.status === 'manual' ||
        data.status === 'off-platform'
      ) {
        acc[platform as SOCIAL_PLATFORMS] = getEmailTemplPlatformData(
          platform as SOCIAL_PLATFORMS,
          data,
        );
      }
      return acc;
    },
    {},
  );

export const getTemplateData = (
  creator: ICreator,
  foamTalentLocationFFEnabled: boolean,
) => {
  const { age, dateOfBirth } = creator;
  const location = foamTalentLocationFFEnabled
    ? getCreatorLocation(creator)
    : creator.city;
  const dobAge = dateOfBirth
    ? new Date().getFullYear() - new Date(dateOfBirth).getFullYear()
    : null;
  // @ts-ignore
  const { avatar, name } = getMainPlatformData(creator);
  const platforms = getPlatformsDataForEmailTemp(
    creator.platforms,
    creator.isOffPlatform,
  );

  return {
    age: age || dobAge || undefined,
    avatar,
    location,
    name,
    platforms,
  };
};

export const getBasicEmailFromCreators = (
  creators: ICreator[],
  alignmentFFEnabled: boolean,
  foamTalentLocationFFEnabled: boolean,
) => {
  const template = creators.map((creator) =>
    alignmentFFEnabled
      ? getBasicEmailFromCreatorAligned(creator, foamTalentLocationFFEnabled)
      : getBasicEmailFromCreatorNotAligned(
          creator,
          foamTalentLocationFFEnabled,
        ),
  );
  const stringifiedTemplate = template.join('');

  return stringifiedTemplate;
};

export const getBasicEmailFromCreator = (
  creator: ICreator,
  foamEmbedsAlignment: boolean,
  foamTalentLocationFFEnabled: boolean,
) =>
  foamEmbedsAlignment
    ? getBasicEmailFromCreatorAligned(creator, foamTalentLocationFFEnabled)
    : getBasicEmailFromCreatorNotAligned(creator, foamTalentLocationFFEnabled);

// TODO: this function should be removed once FOAM-7557 and FOAM-7349 are released and tested, also foam_embeds_alignment FF
export const getBasicEmailFromCreatorNotAligned = (
  creator: ICreator,
  foamTalentLocationFFEnabled: boolean,
) => {
  const { age, avatar, location, name, platforms } = getTemplateData(
    creator,
    foamTalentLocationFFEnabled,
  );

  return `
    <table style="font-family: Arial, sans-serif; margin-bottom: 16px; max-width: 624px; border-spacing: 0;">
    <tbody>
      <tr>
        <td style="display: block; overflow: hidden; border-radius: 8px; width: 64px; height: 64px;"> 
          <img src="${
            avatar
              ? avatar
              : 'https://assets.usefoam.com/extension/avatar-icon.png'
          }" alt="${name}" width="64" height="64" style="display: block; width: 64px; max-width: 64px; height: auto;" />
        </td>
        <td style="width: 100%; padding-left: 16px;">
          <p style="margin: 0 0 4px; color: #1C2128; font-size: 13px; line-height: 1; font-weight: bold;">${name}</p>
          <p style="margin: 0; line-height: 1;">
          ${
            location
              ? `<span style="margin-right: 12px; color: #54657D; font-size: 11px;">${location}</span>`
              : ''
          }
          ${
            age
              ? `<span style="color: #8B94A2; font-size: 11px;">${age}</span>`
              : ''
          }
          </p>

          ${
            platforms && Object.keys(platforms).length
              ? `<table style="border-spacing: 0; margin-top: 4px;">
                <tbody>
                    <tr style="padding: 0px;">
                        ${
                          platforms.instagram
                            ? `
                        <td valign="center" style="vertical-align: middle; padding: 0px; padding-right: 16px; padding-bottom: 4px; line-height: 1;">
                            <span style="color: #3A495F; font-size: 11px; font-weight: bold;">Instagram:</span>
                            <span
                            style="margin-right: 12px; padding-left: 4px; color: #3A495F; font-size: 11px;">${
                              platforms.instagram.status === 'manual'
                                ? ''
                                : platforms.instagram.followersCount +
                                  ' followers'
                            }
                           </span>
                        </td>`
                            : ''
                        }
                        ${
                          platforms.tiktok
                            ? `
                        <td valign="center" style="vertical-align: middle; padding: 0px; padding-right: 16px; padding-bottom: 4px; line-height: 1;">
                            <span style="color: #3A495F; font-size: 11px; font-weight: bold;">TikTok:</span>
                            <span
                            style="margin-right: 12px; padding-left: 4px; color: #3A495F; font-size: 11px;">${
                              platforms.tiktok.status === 'manual'
                                ? ''
                                : platforms.tiktok.followersCount + ' followers'
                            }
                            </span>
                        </td>`
                            : ''
                        }
                        ${
                          platforms.youtube
                            ? `
                        <td valign="center" style="vertical-align: middle; padding: 0px; padding-right: 16px; padding-bottom: 4px; line-height: 1;">
                            <span style="color: #3A495F; font-size: 11px; font-weight: bold;">YouTube:</span>
                            <span
                            style="margin-right: 12px; padding-left: 4px; color: #3A495F; font-size: 11px;">${platforms.youtube.followersCount}
                            subscribers</span>
                        </td>`
                            : ''
                        }
                        ${
                          platforms.snapchat
                            ? `
                        <td valign="center" style="vertical-align: middle; padding: 0px; padding-right: 16px; padding-bottom: 4px; line-height: 1;">
                            <span style="color: #3A495F; font-size: 11px; font-weight: bold;">Snapchat:</span>
                            <span
                            style="margin-right: 12px; padding-left: 4px; color: #3A495F; font-size: 11px;">${
                              platforms.snapchat.status === 'manual'
                                ? ''
                                : platforms.snapchat.followersCount +
                                  ' followers'
                            }
                            </span>
                        </td>`
                            : ''
                        }
                    </tr>
                    <tr style="padding: 0px">
                        ${
                          platforms.instagram
                            ? `
                        <td valign="center" style="vertical-align: middle; padding: 0px; padding-right: 16px; line-height: 1;">
                            <a href="${platforms.instagram.platformUrl}" target="_blank"
                            style="margin: 0; color: #F8A34F; font-size: 11px; text-decoration: underline; line-height: 1;">@${platforms.instagram.handle}</a>
                        </td>`
                            : ''
                        }
                        ${
                          platforms.tiktok
                            ? `
                        <td valign="center" style="vertical-align: middle; padding: 0px; padding-right: 16px; line-height: 1;">
                            <a href="${platforms.tiktok.platformUrl}" target="_blank"
                            style="margin: 0; color: #F8A34F; font-size: 11px; text-decoration: underline; line-height: 1;">@${platforms.tiktok.handle}</a>
                        </td>`
                            : ''
                        }
                        ${
                          platforms.youtube
                            ? `
                        <td valign="center" style="vertical-align: middle; padding: 0px; padding-right: 16px; line-height: 1;">
                            <a href="${
                              platforms.youtube.platformUrl
                            }" target="_blank"
                            style="margin: 0; color: #F8A34F; font-size: 11px; text-decoration: underline; line-height: 1;">@${
                              platforms.youtube.name || platforms.youtube.handle
                            }</a>
                        </td>`
                            : ''
                        }
                        ${
                          platforms.snapchat
                            ? `
                        <td valign="center" style="vertical-align: middle; padding: 0px; padding-right: 16px; line-height: 1;">
                            <a href="${
                              platforms.snapchat.platformUrl
                            }" target="_blank"
                            style="margin: 0; color: #F8A34F; font-size: 11px; text-decoration: underline; line-height: 1;">@${
                              platforms.snapchat.name ||
                              platforms.snapchat.handle
                            }</a>
                        </td>`
                            : ''
                        }
                    </tr>
                </tbody>
            </table>`
              : ''
          }
        </td>
      </tr>
    </tbody>
  </table>
  
  ${
    creator.bioAgency?.length || creator.notes?.length
      ? `<table style="font-family: Arial, sans-serif; max-width: 624px; border-spacing: 0;">
    <tbody>
      ${
        creator.bioAgency?.length
          ? `<tr>
          <td>
            <p style="margin: 0; line-height: 1; margin-bottom: 16px;">${creator.bioAgency}</p>
          </td>
      </tr>`
          : ''
      }
      ${
        creator.notes?.length
          ? `<tr>
            <td>
              <p style="margin: 0; line-height: 1; margin-bottom: 16px;">${creator.notes}</p>
            </td>
        </tr>`
          : ''
      }
    </tbody>
  </table>`
      : ''
  }

  ${
    creator.webLinks?.length
      ? `<table style="font-family: Arial, sans-serif; max-width: 624px; border-spacing: 0;">
    <tbody>
     <tr>
     ${
       creator.webLinks[0]
         ? `<td style="width: 25px">
            <a href="${creator.webLinks[0]}" target="_blank">
              <img src='https://www.google.com/s2/favicons?domain=${creator.webLinks[0]}&sz=16'/>
            </a>
        </td>`
         : ''
     }
     ${
       creator.webLinks[1]
         ? `<td style="width: 25px">
          <a href="${creator.webLinks[1]}" target="_blank">
            <img src='https://www.google.com/s2/favicons?domain=${creator.webLinks[1]}&sz=16'/>
          </a>
        </td>`
         : ''
     }
     ${
       creator.webLinks[2]
         ? `<td style="width: 25px">
          <a href="${creator.webLinks[2]}" target="_blank">
            <img src='https://www.google.com/s2/favicons?domain=${creator.webLinks[2]}&sz=16'/>
          </a>
        </td>`
         : ''
     }
     ${
       creator.webLinks[3]
         ? `<td style="width: 25px">
          <a href="${creator.webLinks[3]}" target="_blank">
            <img src='https://www.google.com/s2/favicons?domain=${creator.webLinks[3]}&sz=16'/>
          </a>
        </td>`
         : ''
     }
      </tr>
    </tbody>
  </table>`
      : ''
  }

  <br>
  `;
};

export const getBasicEmailFromCreatorAligned = (
  creator: ICreator,
  foamTalentLocationFFEnabled: boolean,
) => {
  const { age, avatar, location, name, platforms } = getTemplateData(
    creator,
    foamTalentLocationFFEnabled,
  );

  return `
    <table cellpadding=0 cellspacing=0 style="font-family: Arial, sans-serif; margin-bottom: 16px; max-width: 550px;">
    <tbody>
      <tr>
        <td style="display: block; overflow: hidden; border-radius: 8px; width: 64px; height: 64px;"> 
          <img src="${
            avatar
              ? avatar
              : 'https://assets.usefoam.com/extension/avatar-icon.png'
          }" alt="${name}" width="64" height="64" style="display: block;" />
        </td>
        <td style="padding-left: 16px;">
          <div style="height: 13px;">
            <p style="margin: 0; color: #1C2128; font-size: 13px; line-height: 1; font-weight: bold;">${name}</p>
          </div>
          <div style="height: 13px;">
            <p style="margin: 0;">
            ${
              location
                ? `<span style="margin-right: 12px; color: #54657D; font-size: 11px; line-height: 1;"">${location}</span>`
                : ''
            }
            ${
              age
                ? `<span style="color: #8B94A2; font-size: 11px; line-height: 1;">${age}</span>`
                : ''
            }
            </p>
          </div>

          ${
            platforms && Object.keys(platforms).length
              ? `<table cellpadding=0 cellspacing=0 style="margin-top: 5px;">
                <tbody>
                    <tr>
                      ${
                        platforms.instagram
                          ? `
                          <td valign="center" style="vertical-align: middle; padding-right: 16px;">
                            <div style="height: 16px">
                              <img width="16" height="16" src="https://assets.usefoam.com/extension/instagram-color-icon.png" style="vertical-align: middle;" alt="Instagram icon" />
                            <span
                              style="color: #3A495F; font-size: 11px; line-height: 1;">${
                                platforms.instagram.status === 'manual'
                                  ? '-'
                                  : platforms.instagram.followersCount
                                    ? platforms.instagram.followersCount
                                    : '-'
                              }
                            </span>
                          </div>
                        </td>`
                          : ''
                      }
                      ${
                        platforms.tiktok
                          ? `
                          <td valign="center" style="vertical-align: middle; padding-right: 16px;">
                            <div style="height: 16px">
                              <img width="16" height="16" src="https://assets.usefoam.com/extension/tiktok-color-icon.png" style="vertical-align: middle;" alt="TikTok icon" />
                              <span style="color: #3A495F; font-size: 11px; line-height: 1;">${
                                platforms.tiktok.status === 'manual'
                                  ? '-'
                                  : platforms.tiktok.followersCount
                                    ? platforms.tiktok.followersCount
                                    : '-'
                              }
                              </span>
                            </div>
                          </td>`
                          : ''
                      }
                      ${
                        platforms.youtube
                          ? `
                          <td valign="center" style="vertical-align: middle; padding-right: 16px;">
                            <div style="height: 16px">
                              <img width="16" height="16" src="https://assets.usefoam.com/extension/youtube-color-icon.png" style="vertical-align: middle;" alt="Youtube icon" />
                              <span style="color: #3A495F; font-size: 11px; line-height: 1;">${
                                platforms.youtube.followersCount
                                  ? platforms.youtube.followersCount
                                  : '-'
                              }</span>
                              </div>
                          </td>`
                          : ''
                      }
                        ${
                          platforms.snapchat
                            ? `
                          <td valign="center" style="vertical-align: middle; padding-right: 16px;">
                            <div style="height: 16px">
                              <img width="16" height="16" src="https://assets.usefoam.com/extension/snapchat-color-icon.png" style="vertical-align: middle;" alt="Snapshat icon" />
                              <span style="color: #3A495F; font-size: 11px; line-height: 1;">${
                                platforms.snapchat.status === 'manual'
                                  ? '-'
                                  : platforms.snapchat.followersCount
                                    ? platforms.snapchat.followersCount
                                    : '-'
                              }
                              </span>
                            </div>
                          </td>`
                            : ''
                        }
                    </tr>
                    <tr>
                      ${
                        platforms.instagram
                          ? `
                          <td valign="center" style="vertical-align: middle; padding-right: 16px;">
                            <div style="margin-top: 2px">
                              <a href="${platforms.instagram.platformUrl}" target="_blank" style="margin: 0; color: #155FEF; font-size: 11px; text-decoration: underline; line-height: 1;">@${platforms.instagram.handle}</a>
                            </div>
                          </td>`
                          : ''
                      }
                      ${
                        platforms.tiktok
                          ? `
                          <td valign="center" style="vertical-align: middle; padding-right: 16px;">
                            <div style="margin-top: 2px">
                              <a href="${platforms.tiktok.platformUrl}" target="_blank" style="margin: 0; color: #155FEF; font-size: 11px; text-decoration: underline; line-height: 1;">@${platforms.tiktok.handle}</a>
                            </div>
                          </td>`
                          : ''
                      }
                      ${
                        platforms.youtube
                          ? `
                          <td valign="center" style="vertical-align: middle; padding-right: 16px;">
                            <div style="margin-top: 2px">
                              <a href="${
                                platforms.youtube.platformUrl
                              }" target="_blank" style="margin: 0; color: #155FEF; font-size: 11px; text-decoration: underline; line-height: 1;">@${
                                platforms.youtube.name ||
                                platforms.youtube.handle
                              }</a>
                            </div>
                          </td>`
                          : ''
                      }
                      ${
                        platforms.snapchat
                          ? `
                          <td valign="center" style="vertical-align: middle; padding-right: 16px;">
                            <div style="margin-top: 2px">
                              <a href="${
                                platforms.snapchat.platformUrl
                              }" target="_blank" style="margin: 0; color: #155FEF; font-size: 11px; text-decoration: underline; line-height: 1;">@${
                                platforms.snapchat.name ||
                                platforms.snapchat.handle
                              }</a>
                            </div>
                          </td>`
                          : ''
                      }
                    </tr>
                </tbody>
            </table>`
              : ''
          }
        </td>
      </tr>
    </tbody>
  </table>
  
  ${
    creator.bioAgency?.length || creator.notes?.length
      ? `
      ${
        creator.bioAgency?.length
          ? `<p style="max-width: 550px; font-size: 13px; font-weight: 400; line-height: 16px; color: #1C2128; margin-bottom: 0;">${creator.bioAgency}</p>`
          : ''
      }
      ${
        creator.notes?.length
          ? `<p style="max-width: 550px; font-size: 13px; font-weight: 400; line-height: 16px; color: #1C2128; margin-bottom: 0;">${creator.notes}</p>`
          : ''
      }`
      : ''
  }

  ${
    creator.webLinks?.length
      ? `<div style="margin-top: 16px; margin-bottom: 4px; height: 16px;">
      <table cellpadding=0 cellspacing=0 style="font-family: Arial, sans-serif; max-width: 550px; height: 100%;">
        <tbody>
        <tr>
        ${creator.webLinks
          .map(
            (weblink) =>
              `<td style="width: 16px; height: 16px; padding-right: 16px;">
              <a href="${weblink}" target="_blank">
                <img style="border-radius: 2px;" src='https://www.google.com/s2/favicons?domain=${weblink}&sz=16'/>
              </a>
            </td>`,
          )
          .join('')}      
          </tr>
        </tbody>
      </table>
    </div>`
      : ''
  }

  <br>
  `;
};
